// Performance optimization utilities

/**
 * Lazy load images when they enter viewport
 */
export const lazyLoadImages = () => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          observer.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
};

/**
 * Preload critical resources
 */
export const preloadCriticalResources = () => {
  const criticalResources = [
    { href: '/static/css/main.css', as: 'style' },
  ];

  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as;
    document.head.appendChild(link);
  });
};

/**
 * Defer non-critical CSS
 */
export const deferNonCriticalCSS = () => {
  const loadDeferredStyles = () => {
    const addStylesNode = document.getElementById('deferred-styles');
    if (addStylesNode) {
      const replacement = document.createElement('div');
      replacement.innerHTML = addStylesNode.textContent;
      document.body.appendChild(replacement);
      addStylesNode.parentElement.removeChild(addStylesNode);
    }
  };

  const raf = window.requestAnimationFrame || window.mozRequestAnimationFrame ||
    window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;
  
  if (raf) {
    raf(() => {
      window.setTimeout(loadDeferredStyles, 0);
    });
  } else {
    window.addEventListener('load', loadDeferredStyles);
  }
};

/**
 * Optimize third-party scripts loading
 */
export const optimizeThirdPartyScripts = () => {
  // Delay loading of non-critical third-party scripts
  window.addEventListener('load', () => {
    setTimeout(() => {
      // Load analytics and other non-critical scripts here
      console.log('Third-party scripts loaded after page load');
    }, 3000);
  });
};

/**
 * Enable service worker for caching
 */
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then(registration => {
          console.log('SW registered: ', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

/**
 * Reduce layout shifts by setting image dimensions
 */
export const preventLayoutShift = () => {
  document.querySelectorAll('img:not([width]):not([height])').forEach(img => {
    if (img.naturalWidth && img.naturalHeight) {
      img.setAttribute('width', img.naturalWidth);
      img.setAttribute('height', img.naturalHeight);
    }
  });
};

/**
 * Optimize font loading
 */
export const optimizeFontLoading = () => {
  if ('fonts' in document) {
    Promise.all([
      document.fonts.load('1em Inter'),
      document.fonts.load('700 1em Inter'),
    ]).then(() => {
      document.documentElement.classList.add('fonts-loaded');
    });
  }
};
